/**
 * /federation/partners/:id — one partner's credentials and its trail.
 *
 * Rotate, revoke and delete were row actions on a list. They are not list-shaped
 * work: rotate invalidates the partner's current token the instant it runs, and
 * hands back a replacement shown exactly once. That belongs on the record, with
 * room to say what it does, which is the same reason task triage and plugin
 * release are their own screens rather than a menu on a row.
 *
 * Approve and reject are deliberately absent. A pending partner is the review
 * queue's business, and having the decision in two places is how it gets made
 * twice.
 */
import { ApiError } from '@optum/api-client/client';
import {
  deleteFederatedPartner,
  listFederatedPartners,
  revokeFederatedPartner,
  rotateFederatedPartnerToken,
} from '@optum/api-client/endpoints/federated-partners';
import { listAuditEvents } from '@optum/api-client/endpoints/federation';
import type { FederatedPartner, FederatedPartnerWithSecret } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { Button } from '../../ui/Button.js';
import { useStepUp } from '../../lib/step-up.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { expiryStatus, inOverlap } from '../../lib/token-expiry.js';
import { qk } from '../../lib/query-keys.js';
import { Band } from '../../ui/Band.js';
import { useConfirm } from '../../ui/Confirm.js';
import { RecordShape, ShapeError, ShapeLoading } from '../../ui/shapes.js';
import { DataTable } from '../../ui/DataTable.js';
import { Avatar, Badge, Typography } from '../../ui/finixt.js';
import { WebhookCard } from '../webhooks/WebhookCard.js';

import { PartnerConfigRows } from './PartnerConfigRows.js';
import { PartnerSecretsEditor } from './PartnerSecretsEditor.js';
import { PartnerTokenDialog } from './PartnerTokenDialog.js';

export function PartnerDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const { isPlatformAdmin } = useAuth();
  const [issued, setIssued] = useState<FederatedPartnerWithSecret | null>(null);

  // There is no GET /federated-partners/{id}; the list is the only read, and it
  // is already cached by the page that linked here.
  const query = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });
  const partner = (query.data ?? []).find((p) => p.id === id);
  useBreadcrumbTail(partner?.name);

  if (query.isPending) {
    return <ShapeLoading shape="record" />;
  }
  if (query.isError) {
    return <ShapeError shape="record" error={query.error} fallback="Failed to load" />;
  }
  if (!partner) {
    return <ShapeError shape="record" error={null} fallback="No partner with that id." />;
  }

  return (
    <RecordShape
      back={{ to: '/federation', label: 'Federation' }}
      avatar={<Avatar initials={partner.name.slice(0, 2).toUpperCase()} size={48} />}
      eyebrow={
        <>
          <Typography variant="eyebrow">Partner</Typography>
          <StatusBadge status={partner.status} vocabulary="federatedPartner" />
          {partner.status === 'active' && <PartnerExpiryBadge partner={partner} />}
        </>
      }
      title={partner.name}
      subtitle={
        <Typography variant="meta" mono as="span">
          {partner.idp_name}
        </Typography>
      }
    >
      <Band label="Details" style={{ marginBottom: 11 }} />
      <div
        style={{
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-card)',
          padding: 16,
        }}
      >
        {(
          [
            ['Description', partner.description ?? '—'],
            ['Contact', partner.contact_email ?? '—'],
            ['Approved', partner.approved_at ? absoluteDateTime(partner.approved_at) : '—'],
            ['Token rotated', partner.rotated_at ? absoluteDateTime(partner.rotated_at) : 'never'],
            ['Token expires', expiryStatus(partner.expires_at).label],
            ['Revoked', partner.revoked_at ? absoluteDateTime(partner.revoked_at) : '—'],
          ] as const
        ).map(([label, value]) => (
          <div
            key={label}
            style={{
              display: 'grid',
              gridTemplateColumns: '150px minmax(0,1fr)',
              gap: 11,
              paddingTop: 5,
              paddingBottom: 5,
            }}
          >
            <span style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
              {label}
            </span>
            <span
              style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-strong)' }}
              className="mono"
            >
              {value}
            </span>
          </div>
        ))}
        {partner.rejected_reason && (
          <p style={{ marginTop: 11, fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            Rejected: {partner.rejected_reason}
          </p>
        )}
      </div>

      {isPlatformAdmin && (
        <>
          <Band label="Credentials" style={{ marginTop: 30, marginBottom: 11 }} />
          <PartnerCredentials partner={partner} onIssued={setIssued} />

          <Band label="Secrets" style={{ marginTop: 30, marginBottom: 11 }} />
          <PartnerSecretsEditor partnerId={partner.id} />

          <Band label="Configuration rows" style={{ marginTop: 30, marginBottom: 11 }} />
          <PartnerConfigRows partnerId={partner.id} />

          <Band label="Webhook" style={{ marginTop: 30, marginBottom: 11 }} />
          <WebhookCard owner={{ kind: 'federated_partner', id: partner.id }} />
        </>
      )}

      <Band label="Activity" style={{ marginTop: 30, marginBottom: 11 }} />
      <PartnerActivity idpName={partner.idp_name} />

      <PartnerTokenDialog issued={issued} onClose={() => setIssued(null)} />
    </RecordShape>
  );
}

function PartnerCredentials({
  partner,
  onIssued,
}: {
  partner: FederatedPartner;
  onIssued: (issued: FederatedPartnerWithSecret) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const confirm = useConfirm();
  // A rotation returns the new master token, so the agent asks for a fresh sign-in.
  const [guardedRotate, rotateStepUp] = useStepUp(rotateFederatedPartnerToken);
  const rotate = useMutation({
    mutationFn: () => guardedRotate(partner.id),
    onSuccess: (issued) => {
      toast.success(`Rotated ${partner.name}`);
      qc.invalidateQueries({ queryKey: qk.federatedPartners() });
      onIssued(issued);
    },
    onError: (e) => toast.error('Rotate failed', e instanceof ApiError ? e.message : String(e)),
  });
  const revoke = useMutation({
    mutationFn: () => revokeFederatedPartner(partner.id),
    onSuccess: () => {
      toast.success(`Revoked ${partner.name}`);
      qc.invalidateQueries({ queryKey: qk.federatedPartners() });
    },
    onError: (e) => toast.error('Revoke failed', e instanceof ApiError ? e.message : String(e)),
  });
  const remove = useMutation({
    mutationFn: () => deleteFederatedPartner(partner.id),
    onSuccess: () => {
      toast.success(`Deleted ${partner.name}`);
      qc.invalidateQueries({ queryKey: qk.federatedPartners() });
      navigate('/federation');
    },
    onError: (e) => toast.error('Delete failed', e instanceof ApiError ? e.message : String(e)),
  });

  const isActive = partner.status === 'active';
  const isGone = partner.status === 'rejected' || partner.status === 'revoked';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 16,
      }}
    >
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        {isActive
          ? 'The master token authenticates every request this partner makes. It is never readable after issue — rotating mints a replacement, shows it once, and keeps the old one working for a grace period.'
          : 'This partner holds no working token.'}
      </p>
      {isActive && inOverlap(partner.previous_retires_at) && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--warn)' }}>
          The token this partner held before the last rotation is still accepted, until{' '}
          {absoluteDateTime(partner.previous_retires_at ?? '')}. Two secrets work until then.
        </p>
      )}
      <div style={{ display: 'flex', gap: 7 }}>
        {isActive && (
          <>
            <Button
              size="sm"
              variant="secondary"
              disabled={rotate.isPending}
              onClick={() => {
                void confirm({
                  title: `Rotate ${partner.name}’s master token?`,
                  description:
                    'The new token is shown once. The current one keeps working for a grace period, so the partner can cut over rather than be cut off, but both are live until it retires.',
                  confirmLabel: 'Rotate',
                  tone: 'warn',
                }).then((ok) => {
                  if (ok) rotate.mutate();
                });
              }}
            >
              Rotate token
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={revoke.isPending}
              onClick={() => {
                void confirm({
                  title: `Revoke ${partner.name}?`,
                  description:
                    'All requests with this partner’s token will start failing. The Optum orgs they provisioned remain; only the master token is invalidated.',
                  confirmLabel: 'Revoke',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) revoke.mutate();
                });
              }}
            >
              Revoke
            </Button>
          </>
        )}
        {isGone && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
          >
            Delete record
          </Button>
        )}
      </div>
      {rotateStepUp}
    </div>
  );
}

/** The audit log already filters by `idp_name`, which is what makes this cheap. */
function PartnerActivity({ idpName }: { idpName: string }) {
  const query = useQuery({
    queryKey: qk.federationAudit({ idp_name: idpName, limit: 20 }),
    queryFn: () => listAuditEvents({ idp_name: idpName, limit: 20 }),
  });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) return <ErrorBlock message="Could not load activity." />;
  const events = query.data ?? [];
  if (events.length === 0) return <EmptyBlock>No activity recorded for this partner.</EmptyBlock>;

  return (
    <DataTable
      count={`${events.length} events`}
      hint="newest first"
      rows={events}
      columns={[
        { key: 'event_type', label: 'Event', mono: true, strong: true },
        { key: 'external_sub', label: 'Subject', mono: true, render: (e) => e.external_sub ?? '—' },
        {
          key: 'created_at',
          label: 'When',
          align: 'right',
          mono: true,
          render: (e) => absoluteDateTime(e.created_at),
        },
      ]}
    />
  );
}

function PartnerExpiryBadge({ partner }: { partner: FederatedPartner }) {
  const { state, label, tone } = expiryStatus(partner.expires_at);
  // An undated partner is the pre-Feature-42 norm and says so on the Details
  // band already; a neutral badge beside the status would just be noise.
  if (state === 'none') return null;
  return <Badge tone={tone}>{label}</Badge>;
}
